import React, { useState, useEffect } from 'react';
import { Button, Container, Input } from '@bsf/force-ui';
import { __ } from "@wordpress/i18n";
import { ChevronLeft, Upload, ArrowRight, Check, Trash2 } from "lucide-react";

const General = ({ setCurrentStep }) => {

    const [generalSettings, setGeneralSettings] = useState({
        siteLogoCustom: '',      // This will store ID
        siteLogoCustomURL: '',
    });

    const [siteRepresent, setSiteRepresent] = useState('');

    const defaultFieldValues = {
        sitename: '',
        personname: '',
        organization: '',
        // Add more default fields as needed
    };

    const [fieldValues, setFieldValues] = useState(() => {
        const saved = localStorage.getItem('fieldValues');
        const parsedSaved = saved ? JSON.parse(saved) : {};
        return { ...defaultFieldValues, ...parsedSaved }; // Merge defaults with saved values
    });

    // Single function to fetch all necessary data
    useEffect(() => {
        const getGeneralSettings = () => {
            fetch(schemaSettingsData.ajax_url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'get_general_settings',
                    _ajax_nonce: schemaSettingsData.ajax_nonce,
                }),
            })
                .then((response) => response.json())
                .then((result) => {
                    if (result.success) {
                        const settingsData = result.data; // Assuming result.data is an object with all required fields

                        // Set all the state variables
                        setGeneralSettings({
                            siteLogoCustom: settingsData.siteLogoCustom,
                            siteLogoCustomURL: settingsData.siteLogoCustomURL,
                        });

                        setSiteRepresent(settingsData.siteRepresent || '');

                        setFieldValues((prev) => ({
                            ...prev,
                            sitename: settingsData.sitename || '',
                            personname: settingsData.personname || '',
                            organization: settingsData.organization || '',
                        }));
                    } else {
                        console.error('Error:', result.data);
                    }
                })
                .catch((error) => console.error('AJAX error:', error));
        };

        getGeneralSettings(); // Call the function on component mount
    }, []); // Empty dependency array ensures this runs only once

    // Store the state values in localStorage
    useEffect(() => {
        localStorage.setItem('siteRepresent', siteRepresent);
        localStorage.setItem('fieldValues', JSON.stringify(fieldValues));
    }, [siteRepresent, fieldValues]);

    const handleChange = (name, value) => {
        if (name === 'siteLogoCustom') {
            setGeneralSettings(prev => ({
                ...prev,
                siteLogoCustom: value.id,
                siteLogoCustomURL: value.url,
            }));
        } else {
            setGeneralSettings(prev => ({ ...prev, [name]: value }));
        }
    };


    const saveGeneralSettings = () => {
        const payload = {
            siteRepresent,
            siteLogoCustom: generalSettings.siteLogoCustom, // ID gets saved
            ...fieldValues,
        };

        fetch(schemaSettingsData.ajax_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                action: 'save_general_settings',
                generalSettings: JSON.stringify(payload),
                _ajax_nonce: schemaSettingsData.ajax_nonce, // Pass this nonce from PHP to JS
            }),
        })
            .then(response => response.json())
            .then(result => {
                if (result.success) {
                    setCurrentStep(3); // move to next step
                } else {
                    console.error(result.data);
                }
            })
            .catch(error => console.error('AJAX error:', error));
    };

    const options = [
        { label: 'Personal Website', value: 'person' },
        { label: 'Business Website', value: 'Otherbusiness' },
        { label: 'Organization', value: 'organization' },
        { label: 'Personal Blog', value: 'personblog' },
        { label: 'Community Blog/News Website', value: 'Smallbusiness' },
        { label: 'Webshop', value: 'Webshop' },
    ];

    // Shared field groups
    const inputFieldGroups = {
        websiteOwner: ["Website Owner Name"],
        orgTypeGroup: ["Name", "Organization Type"]
    };

    // Map each option value to a group
    const inputLabels = {
        person: "websiteOwner",
        personblog: "websiteOwner",
        Otherbusiness: "orgTypeGroup",
        organization: "orgTypeGroup",
        Smallbusiness: "orgTypeGroup",
        Webshop: "orgTypeGroup"
    };

    const organizationTypes = [
        "General/Other", "Corporation", "Airline", "Consortium",
        "Educational Organization", "Govt Organisation", "Medical Organisation",
        "NGO", "Performing Group", "News Media Organization", "Project",
        "Sports Organization", "Library System", "Workers Union"
    ];

    const subTypes = {
        "Educational Organization": [
            "College or University", "Elementary School", "High School", "Pre School", "Middle School", "School"
        ],
        "Medical Organisation": ["Diagonastic Lab", "Veterinary Care"],
        "Performing Group": ["Dance Group", "Music Group", "Theater Group"],
        "Project": ["Research Project", "Funding Agency"],
        "Sports Organization": ["Sports Team"]
    };

    const handleInputChange = (name, value) => {
        setFieldValues(prev => ({ ...prev, [name]: value }));
    };

    const renderInputOrDropdown = (label, index) => {
        if (label === "Organization Type") {
            let fieldname = 'organization';
            return (
                <select
                    key={index}
                    name="organization"
                    value={fieldValues[fieldname] || ''}
                    onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                    className="w-full max-w-full border px-2 rounded"
                    style={{
                        height: '48px',
                        borderColor: '#e0e0e0',
                        outline: 'none',
                        boxShadow: 'none',
                        color: fieldValues[label] ? '#363b46' : '#363b46'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#E62A5E'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                >
                    <option value="" disabled>{__("Select Organization Type", "wp-schema-pro")}</option>
                    {organizationTypes.map((type) => (
                        <React.Fragment key={type}>
                            <option value={type}>{type}</option>
                            {subTypes[type]?.map(sub => (
                                <option key={sub} value={sub}>— {sub}</option>
                            ))}
                        </React.Fragment>
                    ))}
                </select>
            );
        } else {
            let fieldname = '';
            if (
                label === "Business Name" ||
                label === "Organization Name" ||
                label === "Webshop Name" ||
                label === "Blog Website Name"
            ) {
                fieldname = "sitename"; // single field name shared for all
            } else if (label === "Website Owner Name") {
                fieldname = "personname"; // single field name shared for all

            }
            return (
                <input
                    key={index}
                    type="text"
                    name={fieldname}
                    placeholder={`Enter ${label}`}
                    value={fieldValues[fieldname] || ''}
                    onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                    className="w-full border px-2 rounded"
                    style={{
                        height: '48px',
                        borderColor: '#e0e0e0',
                        outline: 'none',
                        boxShadow: 'none',
                        color: '#363b46'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#E62A5E'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
            );
        }
    };

    // Render function to conditionally display the inputs
    const renderFields = () => {
        const selectedGroup = inputLabels[siteRepresent];
        const labels = inputFieldGroups[selectedGroup] || [];

        return labels.map((label, index) => renderInputOrDropdown(label, index));
    };
    return (
        <div className="bg-background-primary border-[0.5px] border-subtle rounded-xl shadow-sm mb-6  pl-10 pr-10 pb-10  flex flex-col" style={{ maxWidth: '45%', paddingTop: '1rem' }}>
            <div className="px-1">
                <h1 className="text-[#1E293B] mb-2 text-2xl" style={{ lineHeight: '1.3em' }}>
                    {__("Let’s know about your website", "wp-schema-pro")}
                </h1>
                <span className="text-sm font-medium mb-6 block" style={{ lineHeight: '1.5em', color: '#64748B' }}>
                    {__("To help Google understand what your website is about, select the most suitable type for your website below, and fill in the required basic information.", "wp-schema-pro")}
                </span>

                <Container align="stretch" className="flex flex-col bg-background-primary space-y-6" direction="column" style={{ padding: "24px", borderRadius: "8px" }}>

                    <div className='flex flex-col space-y-2'>
                        <div className='text-base w-full font-semibold' style={{ paddingTop: '25px' }} >{__("This Website Represents", "wp-schema-pro")}</div>
                        <select
                            value={siteRepresent}
                            onChange={(e) => setSiteRepresent(e.target.value)}
                            className='w-full max-w-full border px-2 rounded'
                            style={{
                                height: '48px',
                                width: 'full',
                                borderColor: '#e0e0e0',
                                outline: 'none',
                                boxShadow: 'none',
                                color: siteRepresent ? '#363b46' : '#363b46'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#E62A5E'}
                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                        >
                            <option value="" disabled>{__("Select an option", "wp-schema-pro")}</option>
                            {options.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className='flex flex-col space-y-2'>
                        <div className='text-base w-full font-semibold' style={{ paddingTop: '10px' }}>{__("Website Logo", "wp-schema-pro")}</div>
                        {!generalSettings.siteLogoCustomURL ? (
                            <div className="relative flex items-center justify-center w-full">
                                <Upload
                                    className="absolute top-1/2 transform -translate-y-1/2 text-gray-400"
                                    style={{
                                        backgroundColor: '#F9FAFB',
                                        left: '12px',
                                        width: '18px',
                                        height: '18px',
                                    }}
                                />
                                <input
                                    type="text"
                                    name="logo"
                                    placeholder="Select Image"
                                    value={generalSettings.siteLogoCustomURL || ''} // Ensure falsy values are replaced with an empty string
                                    readOnly
                                    onClick={() => {
                                        if (typeof window.wp === 'undefined' || typeof window.wp.media === 'undefined') {
                                            console.error('WordPress Media Uploader is not available.');
                                            return;
                                        }
                                        const mediaUploader = window.wp.media({
                                            title: 'Select Logo',
                                            button: { text: 'Use this image' },
                                            multiple: false,
                                        });

                                        mediaUploader.on('select', () => {
                                            const attachment = mediaUploader.state().get('selection').first().toJSON();
                                            handleChange('siteLogoCustom', { id: attachment.id, url: attachment.url });
                                        });

                                        mediaUploader.open();
                                    }}
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0',
                                        outline: 'none',
                                        boxShadow: 'none',
                                        backgroundColor: '#F9FAFB',
                                        color: '#111827',
                                        cursor: 'pointer',
                                        padding: '0 12px 0 40px', // Add left padding to make space for the icon
                                        width: '100%',
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#E62A5E'}
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                />
                            </div>
                        ) : (
                            <div className="mt-2 flex items-center justify-start gap-2">
                                <img
                                    src={generalSettings.siteLogoCustomURL}
                                    alt="Selected Logo"
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '100px',
                                        objectFit: 'contain',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '10px',
                                    }}
                                />
                                <button
                                    onClick={() => {
                                        if (typeof window.wp === 'undefined' || typeof window.wp.media === 'undefined') {
                                            console.error('WordPress Media Uploader is not available.');
                                            return;
                                        }
                                        const mediaUploader = window.wp.media({
                                            title: 'Select Logo',
                                            button: { text: 'Use this image' },
                                            multiple: false,
                                        });

                                        mediaUploader.on('select', () => {
                                            const attachment = mediaUploader.state().get('selection').first().toJSON();
                                            handleChange('siteLogoCustom', { id: attachment.id, url: attachment.url });
                                        });

                                        mediaUploader.open();
                                    }}
                                    style={{
                                        marginTop: '20px',
                                        backgroundColor: '#E62A5E',
                                        color: '#fff',
                                        padding: '10px 14px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                    }}
                                >   
                                    {__("Change Image", "wp-schema-pro")}
                                </button>
                                <button
                                    onClick={() => handleChange('siteLogoCustom', { id: '', url: '' })}
                                    style={{
                                        marginTop: '20px',
                                        backgroundColor: 'transparent',
                                        color: '#E62A5E',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    title="Remove Image"
                                >
                                    <Trash2 size={20} />
                                </button>
                            </div>
                        )}
                    </div>
                    {siteRepresent && inputLabels[siteRepresent] && inputFieldGroups[inputLabels[siteRepresent]].map((label, index) => {
                        if (label === "Name") {
                            if (siteRepresent === "Otherbusiness") {
                                label = "Business Name";
                            } else if (siteRepresent === "Smallbusiness") {
                                label = "Blog Website Name";
                            } else if (siteRepresent === "Webshop") {
                                label = "Webshop Name";
                            } else if (siteRepresent === "organization") {
                                label = "Organization Name";
                            }
                        }

                        return (
                            <div key={index} className='flex flex-col space-y-2' style={{ paddingTop: '10px' }}>
                                <div className='text-base font-semibold pt-1'>{label}</div>
                                {renderInputOrDropdown(label, index)}
                            </div>
                        );
                    })}

                </Container>

                <div className="flex justify-between items-center pt-1 gap-4 schema-onboarding-bottom" style={{ paddingTop: '30px' }}>
                    <Button
                        className="flex items-center gap-1 schema-remove-ring"
                        icon={<ChevronLeft />}
                        variant="outline"
                        onClick={() => setCurrentStep(1)}
                    >
                        {__("Back", "wp-schema-pro")}
                    </Button>
                    <div className="flex justify-end items-center gap-3">

                        <Button
                            className="schema-remove-ring"
                            variant="ghost"
                            onClick={() => setCurrentStep(3)}
                        >
                            {' '}
                            {__("Skip", "wp-schema-pro")}
                        </Button>
                        <Button
                            className="flex items-center gap-1 schema-remove-ring"
                            icon={<ArrowRight />}
                            iconPosition="right"
                            style={{
                                backgroundColor: "#E62A5E",
                                transition: "background-color 0.3s ease",
                                padding: "12px",
                                outlineColor: "#E62A5E",
                            }}
                            onClick={() => saveGeneralSettings()}
                        >
                            {__(" Continue Setup", "wp-schema-pro")}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default General;
